import React from 'react';
import styles from './day-calendar.module.scss';
import { businessStore } from 'entities/index';
import { observer } from 'mobx-react';
import { Alert, ConfirmDialog } from 'shared/ui';
import { ClickCalendarCard } from './click-calendar-card';
import { Timeline } from './timeline';
import { EmployeeList } from './employee-list';
import { CalendarColumn } from './calendar-column';
import { CreateBookingModal } from './create-booking-modal/create-booking-modal';
import { QuickBooking } from './quick-booking/quick-booking';
import { BreakCreate } from './break-create/break-create';
import { useDayCalendarModel } from '../model/day-calendar.model';

interface IDayCalendarProps {
  date: Date;
  microSlotDuration?: number;
}
export const DayCalendar: React.FC<IDayCalendarProps> = observer(
  ({ date, microSlotDuration = 15 }) => {
    const model = useDayCalendarModel(
      date,
      businessStore.pointId ?? null,
      microSlotDuration
    );

    const timesWidth = model.timesWidth;
    const headerHeight = 50;
    const totalContentWidth = model.calendarDimensions.width + timesWidth;
    const totalContentHeight = model.calendarDimensions.height + headerHeight;

    return (
      <>
        <div
          className={styles.calendar__container}
          ref={model.calendarContainerRef}
          onDoubleClick={model.handleDoubleClick}
          style={
            {
              '--hour-slot-height': `${model.hourSlotHeight}px`,
            } as React.CSSProperties
          }
        >
          <div
            style={
              model.scale !== 1
                ? {
                    width: `${totalContentWidth * model.scale}px`,
                    minHeight: `${totalContentHeight * model.scale}px`,
                  }
                : undefined
            }
          >
            <div className={styles.core__employee} ref={model.coreEmployeeRef}>
              <div
                ref={model.leftBorderRef}
                className={styles.employee__left_border}
              ></div>
              <div ref={model.employeeHeaderRef}>
                <EmployeeList
                  calendarData={model.calendarData}
                  selectedEmployeeId={model.selectedEmployeeId}
                  onToggleEmployeePopup={model.handleToggleEmployeePopup}
                  employeeListRef={model.employeeListRef}
                  employeeCardRefs={model.employeeCardRefs}
                  date={date}
                  onUpdate={() => model.getData(date)}
                  onCloseEmployeePopup={() => model.setSelectedEmployeeId(null)}
                />
              </div>
            </div>

            <div className={styles.calendar_body}>
              <div ref={model.timesContainerRef} className={styles.core__times}>
                {model.dayTimes.map((time, index) => (
                  <div
                    key={time.split(':')[0]}
                    className={styles.time__item}
                    style={{
                      height: `${model.hourSlotHeight}px`,
                      ...(index === 0
                        ? { height: `${model.hourSlotHeight - 10}px` }
                        : {}),
                    }}
                  >
                    {time}
                  </div>
                ))}
              </div>
              <div
                ref={model.calendarScalerRef}
                className={styles.calendar_content}
              >
                <div className={styles.core__calendar} ref={model.calendarRef}>
                  {model.calendarData?.map((employee) => (
                    <CalendarColumn
                      key={employee.employee.id}
                      employee={employee}
                      dayTimes={model.dayTimes}
                      hourSlotHeight={model.hourSlotHeight}
                      microSlotHeight={model.microSlotHeight}
                      microSlotDuration={microSlotDuration}
                      isTimeInRange={model.isTimeInRange}
                      toMinutes={model.toMinutes}
                      timeToMinutes={model.timeToMinutes}
                      getSlotStartTime={model.getSlotStartTime}
                      dragState={model.dragState}
                      setDragState={model.setDragState}
                      setIsDraggingItem={model.setIsDraggingItem}
                      didDrag={model.didDrag}
                      setDidDrag={model.setDidDrag}
                      clearDragTimeout={model.clearDragTimeout}
                      cancelDragListenersRef={model.cancelDragListenersRef}
                      dragTimeoutRef={model.dragTimeoutRef}
                      onBlockClick={model.handleBlockClick}
                      setCreateModalData={model.setCreateModalData}
                      date={date}
                    />
                  ))}
                </div>
                {model.isTimelineVisible && (
                  <Timeline
                    currentTime={model.currentTime}
                    containerStyle={{
                      position: 'absolute',
                      top: `${model.getTimelinePosition(model.currentTime)}px`,
                      left: model.timelineLeft,
                      width: model.timelineWidth,
                    }}
                  />
                )}
              </div>
            </div>
          </div>
          {model.cardData && (
            <ClickCalendarCard
              id={model.cardData.id}
              employeeId={model.cardData.employeeId}
              onClose={model.handleCloseCard}
              cardType={model.cardData.type}
            />
          )}
          {model.createModalData && (
            <CreateBookingModal
              startTime={model.createModalData.startTime}
              duration={model.createModalData.duration}
              employeeId={model.createModalData.employeeId}
              date={model.createModalData.date}
              onClose={() => model.setCreateModalData(null)}
              onCreateBooking={model.handleCreateBooking}
              onCreateBreak={model.handleCreateBreak}
            />
          )}
          {model.selectedBookingData &&
            (() => {
              const data = model.selectedBookingData;
              if (!data) return null;
              const employeeSchedule = model.calendarData?.find(
                (e) => e.employee.id === data.employeeId
              );
              return (
                <QuickBooking
                  startTime={data.startTime}
                  employeeId={data.employeeId}
                  date={data.date}
                  onClose={() => model.setSelectedBookingData(null)}
                  onCreated={() => {
                    model.setSelectedBookingData(null);
                    model.getData(date);
                  }}
                  existingBookings={employeeSchedule?.bookings}
                  existingBreaks={employeeSchedule?.breaks}
                  workDay={employeeSchedule?.workDay}
                />
              );
            })()}
          {model.selectedBreakData &&
            (() => {
              const data = model.selectedBreakData;
              if (!data) return null;
              const employeeSchedule = model.calendarData?.find(
                (e) => e.employee.id === data.employeeId
              );
              return (
                <BreakCreate
                  startTime={data.startTime}
                  employeeId={data.employeeId}
                  date={data.date}
                  onClose={() => model.setSelectedBreakData(null)}
                  onCreated={() => {
                    model.setSelectedBreakData(null);
                  }}
                  existingBookings={employeeSchedule?.bookings}
                  existingBreaks={employeeSchedule?.breaks}
                />
              );
            })()}
        </div>
        {model.pendingMove && (
          <ConfirmDialog
            message={model.pendingMove.message}
            onConfirm={(result) => {
              if (result) model.handleConfirmMove();
              else model.setPendingMove(null);
            }}
            onClose={() => model.setPendingMove(null)}
          />
        )}
        {model.alertMessage && (
          <Alert
            type="error"
            message={model.alertMessage}
            onClose={model.handleCloseAlert}
          />
        )}
      </>
    );
  }
);
